'use client';

import type { IdentityProvidersNames } from '@repo/api-gw-sdk';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect } from 'react';

import { Loader } from '@/components/layout/loading';
import type { LoginHostKind } from '@/contexts/environment';
import { useEnvironment } from '@/contexts/useEnvironment';
import { useFeatureFlags, useLaunchDarkly } from '@/contexts/useFeatureFlags';

import { LoginPage } from './loginPage';

import { LoginV2DomainPage } from '../login-v2/loginV2DomainPage';
import { LoginV2Page } from '../login-v2/loginV2Page';

interface LoginSwitchProps {
  hostKind: LoginHostKind;
  hasAuthToken: boolean;
  hasError: boolean;
  idpData: IdentityProvidersNames;
}

export function LoginSwitch(props: LoginSwitchProps) {
  const { LoginV2Enabled } = useFeatureFlags();
  const { isReady } = useLaunchDarkly();

  if (!isReady) {
    return <Loader />;
  }

  const loginV2Enabled = LoginV2Enabled === true;

  return loginV2Enabled ? (
    <LoginV2Branch
      hostKind={props.hostKind}
      hasAuthToken={props.hasAuthToken}
      idpData={props.idpData}
    />
  ) : (
    <LoginV1Branch hostKind={props.hostKind} hasError={props.hasError} />
  );
}

function LoginV1Branch({
  hostKind,
  hasError,
}: Pick<LoginSwitchProps, 'hostKind' | 'hasError'>) {
  const { clientApiBaseUrl, isDev } = useEnvironment();
  const searchParams = useSearchParams();
  const shouldRedirect = hostKind === 'public-tenant' && !hasError;

  useEffect(() => {
    if (!shouldRedirect) {
      return;
    }
    let url = `${clientApiBaseUrl}/login`;
    const returnUrl = searchParams.get('returnUrl');
    if (returnUrl && !isDev) {
      url += `?returnUrl=${returnUrl}`;
    }
    window.location.replace(url);
  }, [shouldRedirect, clientApiBaseUrl, isDev, searchParams]);

  if (shouldRedirect) {
    return <Loader />;
  }
  return <LoginPage />;
}

function LoginV2Branch({
  hostKind,
  hasAuthToken,
  idpData,
}: Pick<LoginSwitchProps, 'hostKind' | 'hasAuthToken' | 'idpData'>) {
  const { isDev } = useEnvironment();
  const searchParams = useSearchParams();
  const router = useRouter();
  const hasDomain = Boolean(searchParams.get('domain'));
  const isPublicRoot = hostKind === 'public-root';
  const redirectHome = !isPublicRoot && !isDev && hasAuthToken;

  useEffect(() => {
    if (redirectHome) {
      router.replace('/home');
    }
  }, [redirectHome, router]);

  if (isPublicRoot) {
    return <LoginV2DomainPage />;
  }
  if (isDev) {
    return hasDomain ? (
      <LoginV2Page idpData={idpData} />
    ) : (
      <LoginV2DomainPage />
    );
  }
  if (redirectHome) {
    return <Loader />;
  }
  return <LoginV2Page idpData={idpData} />;
}
